import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { APIService } from 'src/app/services/api.service';

import { NotifierService } from 'src/app/services/notifier.service';
import { getENV } from 'src/app/_helpers/helpers';

interface Labels {
  [key: string]: string;
}

interface ColorInfo {
  color: string;
  border: string;
}

interface LabelsColors {
  [key: string]: ColorInfo;
}

@Component({
  selector: 'app-operator-kit-details-page',
  templateUrl: './operator-kit-details-page.component.html',
  styleUrls: ['./operator-kit-details-page.component.css'],
})
export class OperatorKitDetailsPageComponent implements OnInit, OnChanges {
  @Input('kit') kit: any;
  @Output('hideModel') hideModel: EventEmitter<void> = new EventEmitter<void>();
  kitsData: any[] = [];

  labels: Labels = {
    '0': 'Unknown',
    '1': 'In Operation',
    '2': 'Out Of Operation',
    '3': 'Ready to Deploy',
    '4': 'At Swiss Timing',
    '5': 'Defect',
    '6': 'In Transit (To Track)',
    '7': 'In Transit (To TSD)',
    '8': 'OnCourse Test (Faulty Tag)',
    '9': 'OnCourse Test (New Tag)',
    '10': 'Pending Review (@TSD)',
    '11': 'Ready for Repair',
    '12': 'ToBeConfirmed',
    '13': 'Archived',
    '14': 'OnCourse Spare',
    '15': 'In Transit (To Track)',
    '16': 'In Transit (To TSD)',
  };

  labelsColors: LabelsColors = {
    '0': { color: '#fdab3d', border: '#e99729' },
    '1': { color: '#00c875', border: '#00b461' },
    '2': { color: '#df2f4a', border: '#ce3048' },
    '3': { color: '#007eb5', border: '#3db0df' },
    '4': { color: '#9d50dd', border: '#9238af' },
    '5': { color: '#cab641', border: '#c0ab31' },
    '6': { color: '#579bfc', border: '#4387e8' },
    '7': { color: '#ff007f', border: '#e01279' },
    '8': { color: '#9cd326', border: '#89b921' },
    '9': { color: '#ffcb00', border: '#c0ab1b' },
    '10': { color: '#bb3354', border: '#a42d4a' },
    '11': { color: '#401694', border: '#401694' },
    '12': { color: '#a3a3a3', border: '#909090' },
    '13': { color: '#808080', border: '#707070' },
    '14': { color: '#0086c0', border: '#0073a7' },
    '15': { color: '#579bfc', border: '#4387e8' },
    '16': { color: '#ff007f', border: '#e01279' },
  };

  selectedKit: any = null; // Currently selected kit for the modal
  showHistoryModal: boolean = false;
  loading: boolean = false;
  config: any;
  error = '';
  overview: { [key: string]: number } = {
    total: 0,
    in_operation: 0,
    out_of_operation: 0,
    oncourse_test: 0,
    oncourse_spare: 0
  };

  // Track which rows are in edit mode
  editingStatus: { [key: string]: boolean } = {};
  // Track temporary status changes before confirmation
  tempStatus: { [key: string]: string } = {};
  // Track original statuses to detect changes
  originalStatuses: { [key: string]: string } = {};
  // Track which kits have unsaved changes
  pendingChanges: { [key: string]: boolean } = {};

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private apiService: APIService,
    private notifier: NotifierService
  ) {
    this.config = this.route.snapshot.data['config'];
  }

  ngOnInit(): void {
    // this.fetchKitDetails()
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.fetchKitDetails();
  }

  ngOnDestroy(): void {
    this.notifier.loading(false);
  }

  statuses: { name: string; id: string }[] = [
    { name: 'Unknown', id: 'Unknown' },
    { name: 'In Operation', id: 'In Operation' },
    { name: 'Out Of Operation', id: 'Out Of Operation' },
    { name: 'Ready to Deploy', id: 'Ready to Deploy' },
    { name: 'At Swiss Timing', id: 'At Swiss Timing' },
    { name: 'Defect', id: 'Defect' },
    { name: 'OnCourse Test (Faulty Tag)', id: 'OnCourse Test (Faulty Tag)' },
    { name: 'OnCourse Test (New Tag)', id: 'OnCourse Test (New Tag)' },
    { name: 'Pending Review (@TSD)', id: 'Pending Review (@TSD)' },
    { name: 'Ready for Repair', id: 'Ready for Repair' },
    { name: 'ToBeConfirmed', id: 'ToBeConfirmed' },
    { name: 'Archived', id: 'Archived' },
    { name: 'OnCourse Spare', id: 'OnCourse Spare' },
    { name: 'In Transit (To Track)', id: 'In Transit (To Track)' },
    { name: 'In Transit (To TSD)', id: 'In Transit (To TSD)' },
  ];

  // Operator can only change a tag status to these options
  operatorAllowedStatuses: string[] = [
    'In Operation',
    'Out Of Operation',
    'OnCourse Test (Faulty Tag)',
    'OnCourse Test (New Tag)',
    'OnCourse Spare',
    'In Transit (To Track)',
    'In Transit (To TSD)',
  ];

  isOperatorAllowedStatus(status: string): boolean {
    return this.operatorAllowedStatuses.some(s => s.toLowerCase() === status.toLowerCase());
  }

  filters: any = {
    kit: {
      selected: ['ALL'],
    },
  };

  async fetchKitDetails(): Promise<void> {
    this.notifier.loading(true);
    let apiURL: string = `${
      this.config[getENV()].raceAPI
    }/devices/kits?kit_id=${this.kit}`;
    let response = await this.apiService.getDataPromis(apiURL, {}, {});
    this.kitsData = response
      .map(({ VenueState, Kit, ...keepAttrs }) => keepAttrs)
      .sort((a, b) => a.TagID - b.TagID);

    // Store original statuses
    this.originalStatuses = {};
    this.kitsData.forEach(kit => {
      this.originalStatuses[kit.SerialNumber] = kit.Status;
    });

    // Reset overview counts
    this.overview['total'] = this.kitsData.length;
    this.overview['in_operation'] = 0;
    this.overview['out_of_operation'] = 0;
    this.overview['oncourse_test'] = 0;
    this.overview['oncourse_spare'] = 0;

    // Update overview counts
    this.kitsData.forEach((kit) => {
      if (kit.Status.toLowerCase() === 'in operation') {
        this.overview['in_operation']++;
      } else if (kit.Status.toLowerCase() === 'out of operation') {
        this.overview['out_of_operation']++;
      } else if (
        kit.Status.toLowerCase() === 'oncourse test (faulty tag)' ||
        kit.Status.toLowerCase() === 'oncourse test (new tag)' ||
        kit.Status.toLowerCase() === 'in transit (to track)' ||
        kit.Status.toLowerCase() === 'in transit (to tsd)'
      ) {
        this.overview['oncourse_test']++;
      } else if (kit.Status.toLowerCase() === 'oncourse spare') {
        this.overview['oncourse_spare']++;
      }
    });

    this.notifier.loading(false);
  }

  getColor(status: string): string {
    return this.labelsColors[status]?.color || 'transparent';
  }

  getSelectedOptionColor(statusText: string): string {
    const statusKey = this.findStatusKeyByText(statusText);
    return this.getColor(statusKey);
  }

  getSelectedOptionBorderColor(statusText: string): string {
    const statusKey = this.findStatusKeyByText(statusText);
    return this.getBorderColor(statusKey);
  }

  getTextColor(color: string): string {
    // Make text white for dark background colors
    const darkColors = ['#401694', '#bb3354', '#df2f4a', '#808080'];
    return darkColors.includes(color) ? 'white' : 'black';
  }

  findStatusKeyByText(statusText: string): string {
    return Object.keys(this.labels).find(
      (key) => this.labels[key].toLowerCase() === statusText?.toLowerCase()
    ) || '';
  }

  getBorderColor(status: string): string {
    return this.labelsColors[status]?.border || '#ddd'; // Default border color
  }

  getLabel(status: string): string {
    return this.labels[status] || 'Unknown Status';
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  // New methods for the enhanced UI
  toggleEditStatus(serialNumber: string, currentStatus: string): void {
    this.editingStatus[serialNumber] = !this.editingStatus[serialNumber];
    if (this.editingStatus[serialNumber]) {
      this.tempStatus[serialNumber] = currentStatus;
    }
  }

  confirmStatusChange(kit: any): void {
    this.editingStatus[kit.SerialNumber] = false;
    delete this.tempStatus[kit.SerialNumber];

    // Mark as changed if the status is different from original
    if (this.originalStatuses[kit.SerialNumber] !== kit.Status) {
      this.pendingChanges[kit.SerialNumber] = true;
      // Update counts since a status has changed
      this.updateOverviewCounts();
    } else {
      delete this.pendingChanges[kit.SerialNumber];
    }
  }

  cancelStatusChange(kit: any): void {
    kit.Status = this.tempStatus[kit.SerialNumber];
    this.editingStatus[kit.SerialNumber] = false;
    delete this.tempStatus[kit.SerialNumber];
  }

  async submitForm(): Promise<void> {
    this.notifier.loading(true);
    const dataWithoutTagID = this.kitsData.map(({ TagID, ...rest }) => rest);
    this.loading = true;
    const apiUrl: string = `${this.config[getENV()].raceAPI}/devices/kits`;

    try {
      await this.apiService.postDataPromis(apiUrl, dataWithoutTagID);

      alert('Submitted Data!');

      // Reset pending changes after successful submission
      this.pendingChanges = {};

      // Update original statuses to match current status
      this.kitsData.forEach(kit => {
        this.originalStatuses[kit.SerialNumber] = kit.Status;
      });

      // Update overview counts
      this.updateOverviewCounts();

      this.loading = false;
      this.notifier.loading(false);

    } catch (error) {
      console.error('Error:', error);
      alert('Error occurred while submitting data!');
      this.loading = false;
      this.notifier.loading(false);
    }
  }

  openKitHistory(kit: any): void {
    this.selectedKit = kit.SerialNumber;
    this.showHistoryModal = true;
  }

  closeHistoryModal(): void {
    this.showHistoryModal = false;
  }

  filterByStatus(status: string): void {
    if (status.toLowerCase() === 'all') {
      this.filters['kit']['selected'] = ['ALL'];
    } else if (status.toLowerCase() === 'oncourse test') {
      this.filters['kit']['selected'] = [
        'OnCourse Test (Faulty Tag)',
        'OnCourse Test (New Tag)',
        'In Transit (To Track)',
        'In Transit (To TSD)',
      ];
    } else {
      this.filters['kit']['selected'] = [status];
    }
  }

  // Helper method to check if there are any unsaved changes
  hasChanges(): boolean {
    return Object.keys(this.pendingChanges).length > 0;
  }

  // Add a method to update counts when statuses change
  updateOverviewCounts(): void {
    // Reset counts
    this.overview['in_operation'] = 0;
    this.overview['out_of_operation'] = 0;
    this.overview['oncourse_test'] = 0;
    this.overview['oncourse_spare'] = 0;

    // Recalculate
    this.kitsData.forEach((kit) => {
      if (kit.Status.toLowerCase() === 'in operation') {
        this.overview['in_operation']++;
      } else if (kit.Status.toLowerCase() === 'out of operation') {
        this.overview['out_of_operation']++;
      } else if (
        kit.Status.toLowerCase() === 'oncourse test (faulty tag)' ||
        kit.Status.toLowerCase() === 'oncourse test (new tag)' ||
        kit.Status.toLowerCase() === 'in transit (to track)' ||
        kit.Status.toLowerCase() === 'in transit (to tsd)'
      ) {
        this.overview['oncourse_test']++;
      } else if (kit.Status.toLowerCase() === 'oncourse spare') {
        this.overview['oncourse_spare']++;
      }
    });
  }
}
